import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Badge, DataTable, Button, Input, Modal } from '../../../components/ui';
import { RefreshCw, Building2, Plus, KeyRound, Power, Layers3, Store, Tag, Pencil } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { useAuth } from '../../../app/auth';

interface LimiteLocalesInfo {
  max_locales: number;
  usados: number;
  disponibles: number;
}

interface ArranqueTipo { personalizado: string | null; heredado_de?: string | null; reglas: {sorteo: string; aplicable: boolean; paquete: string | null; efectivo: string | null; maximo: number | null}[] }
type Arranques = Record<'NORMAL' | 'REVENTADO', ArranqueTipo>;
const monedaArranque = (valor: string) => `₡${Number(valor).toLocaleString('es-CR')}`;

type TipoSelector = 'PROPIO' | 'NO_PROPIO' | 'MINI_BANCA';

interface CrearLocalForm {
  nombre: string;
  direccion: string;
  telefono: string;
  tipo_selector: TipoSelector;
  grupo_id: string;
  limite_saldo: string;
}

interface LocalResumenItem {
  local_id: string;
  nombre_local: string;
  local_username?: string | null;
  local_activo: boolean;
  local_archivado?: boolean;
  parent_local_id?: string | null;
  es_propio: boolean;
  tipo_local?: string;
  grupo_id?: string | null;
  limite_saldo?: number | null;
}

interface LocalSimpleItem {
  id: string;
  nombre: string;
  activo: boolean;
  es_propio: boolean;
  grupo_id?: string | null;
}

interface EditarLocalForm {
  es_propio: boolean;
  grupo_id: string;
  limite_saldo: string;
}

interface CodigoDinamicoData {
  codigo: string;
  expira_at: string;
  segundos_restantes: number;
}

export const BancaVendedoresPage: React.FC = () => {
  // MISMA experiencia para BANCA y MINI_BANCA.
  // MINI_BANCA administra SUS vendedores internos (parent_local_id = su local)
  // vía /usuarios/minibanca/*; siempre NO PROPIOS, sin grupos ni código dinámico.
  const { user } = useAuth();
  const esMiniBanca = user?.role === 'MINI_BANCA';
  const esBanca = user?.role === 'BANCA';
  const [retirarSeleccionado, setRetirarSeleccionado] = useState<LocalResumenItem | null>(null);
  const retiroEnCurso = useRef(false);
  const [localesResumen, setLocalesResumen] = useState<LocalResumenItem[]>([]);
  const [limiteLocales, setLimiteLocales] = useState<LimiteLocalesInfo>({ max_locales: 0, usados: 0, disponibles: 0 });
  const [loading, setLoading] = useState(false);
  const [savingLocal, setSavingLocal] = useState(false);
  const [savingAction, setSavingAction] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [credModalOpen, setCredModalOpen] = useState(false);
  const [selectedLocal, setSelectedLocal] = useState<LocalResumenItem | null>(null);
  const [editingLocal, setEditingLocal] = useState<LocalResumenItem | null>(null);
  const [credForm, setCredForm] = useState({ username: '', password: '' });
  const [editForm, setEditForm] = useState<EditarLocalForm>({ es_propio: false, grupo_id: '', limite_saldo: '' });
  const [arranques, setArranques] = useState<Record<string, Arranques>>({});
  const [arranqueForm, setArranqueForm] = useState<Record<'NORMAL' | 'REVENTADO', string | null>>({NORMAL: null, REVENTADO: null});
  const [error, setError] = useState('');
  const [eliminarSeleccionado, setEliminarSeleccionado] = useState<LocalResumenItem | null>(null);
  const eliminacionEnCurso = useRef(false);
  const [codigoDinamico, setCodigoDinamico] = useState<CodigoDinamicoData | null>(null);
  const [segundosRestantes, setSegundosRestantes] = useState(0);
  const [copiado, setCopiado] = useState(false);
  const [cargandoCodigo, setCargandoCodigo] = useState(false);
  const [form, setForm] = useState<CrearLocalForm>({
    nombre: '',
    direccion: '',
    telefono: '',
    tipo_selector: 'NO_PROPIO',
    grupo_id: '',
    limite_saldo: '',
  });

  const cargarDatos = async () => {
    setLoading(true);
    setError('');
    try {
      if (esMiniBanca) {
        const resumenData = await fetchAPI<LocalResumenItem[]>('/usuarios/minibanca/vendedores');
        setLocalesResumen(resumenData);
        // El cupo lo controla la banca a nivel global; sin tope visual aquí.
        setLimiteLocales({ max_locales: 0, usados: resumenData.length, disponibles: 1 });
        return;
      }
      const [resumenData, limiteLocalesData, montosData] = await Promise.all([
        fetchAPI<LocalResumenItem[]>('/usuarios/banca/locales/resumen-vendedores'),
        fetchAPI<LimiteLocalesInfo>('/usuarios/banca/locales/limite'),
        fetchAPI<Record<string, Arranques>>('/usuarios/banca/montos-arranque').catch(() => {
          setError('Los locales están disponibles. La configuración de monto de arranque requiere actualizar el backend y preparar su base local.');
          return {} as Record<string, Arranques>;
        }),
      ]);
      setLocalesResumen(resumenData);
      setArranques(montosData);
      setLimiteLocales(limiteLocalesData);
    } catch (e: any) {
      setError(e.message || 'No se pudo cargar el resumen de locales');
    } finally {
      setLoading(false);
    }
  };

  const retirarLocal = async () => {
    if (!esBanca || !retirarSeleccionado || retiroEnCurso.current) return;
    retiroEnCurso.current = true;
    setSavingAction(true);
    setError('');
    try {
      const recurso = retirarSeleccionado.tipo_local === 'MINI_BANCA' ? 'minibancas' : 'locales';
      await fetchAPI(`/usuarios/banca/${recurso}/${retirarSeleccionado.local_id}`, { method: 'DELETE' });
      setRetirarSeleccionado(null);
      await cargarDatos();
    } catch (e: any) {
      setError(e.message || 'No se pudo retirar el acceso');
    } finally {
      retiroEnCurso.current = false;
      setSavingAction(false);
    }
  };

  const eliminarDefinitivamente = async () => {
    if (!esBanca || !eliminarSeleccionado?.local_archivado || eliminacionEnCurso.current) return;
    eliminacionEnCurso.current = true;
    setSavingAction(true);
    setError('');
    try {
      const recurso = eliminarSeleccionado.tipo_local === 'MINI_BANCA' ? 'minibancas' : 'locales';
      await fetchAPI(`/usuarios/banca/${recurso}/${eliminarSeleccionado.local_id}/definitivo`, { method: 'DELETE' });
      setEliminarSeleccionado(null);
      await cargarDatos();
    } catch (e: any) {
      setError(e.message || 'No se pudo eliminar definitivamente');
    } finally {
      eliminacionEnCurso.current = false;
      setSavingAction(false);
    }
  };

  const crearLocal = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.nombre.trim()) {
      setError('Nombre del local es obligatorio');
      return;
    }

    setSavingLocal(true);
    try {
      if (esMiniBanca) {
        // Vendedor interno: SIEMPRE NO PROPIO, asociado a esta Mini Banca.
        await fetchAPI<LocalSimpleItem>('/usuarios/minibanca/vendedores', {
          method: 'POST',
          body: JSON.stringify({
            nombre: form.nombre.trim(),
            direccion: form.direccion.trim() || null,
            telefono: form.telefono.trim() || null,
            limite_saldo: form.limite_saldo.trim() ? Number(form.limite_saldo) : null,
          }),
        });
        setModalOpen(false);
        setForm({ nombre: '', direccion: '', telefono: '', tipo_selector: 'NO_PROPIO', grupo_id: '', limite_saldo: '' });
        await cargarDatos();
        return;
      }
      const es_propio = form.tipo_selector === 'PROPIO';
      const tipo_local = form.tipo_selector === 'MINI_BANCA' ? 'MINI_BANCA' : 'NORMAL';
      await fetchAPI<LocalSimpleItem>('/usuarios/banca/locales', {
        method: 'POST',
        body: JSON.stringify({
          nombre: form.nombre.trim(),
          direccion: form.direccion.trim() || null,
          telefono: form.telefono.trim() || null,
          es_propio,
          tipo_local,
          grupo_id: form.grupo_id.trim() || null,
          limite_saldo: !es_propio && form.tipo_selector !== 'MINI_BANCA' && form.limite_saldo.trim() ? Number(form.limite_saldo) : null,
        }),
      });
      setModalOpen(false);
      setForm({ nombre: '', direccion: '', telefono: '', tipo_selector: 'NO_PROPIO', grupo_id: '', limite_saldo: '' });
      await cargarDatos();
    } catch (e: any) {
      setError(e.message || 'No se pudo crear el local');
    } finally {
      setSavingLocal(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, []);

  const generarCodigoDinamico = async () => {
    setCargandoCodigo(true);
    try {
      const data = await fetchAPI<CodigoDinamicoData>('/auth/codigo-dinamico/regenerar', {
        method: 'POST',
      });
      setCodigoDinamico(data);
      setSegundosRestantes(data.segundos_restantes || 0);
    } catch (e: any) {
      setError(e.message || 'No se pudo generar el código dinámico');
    } finally {
      setCargandoCodigo(false);
    }
  };

  const copiarCodigo = async () => {
    if (!codigoDinamico?.codigo) return;
    try {
      await navigator.clipboard.writeText(codigoDinamico.codigo);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch {
      setError('No se pudo copiar el código');
    }
  };

  // Countdown local — solo decrementa, NO llama API al llegar a 0
  useEffect(() => {
    if (segundosRestantes <= 0) return;
    const iv = window.setInterval(() => {
      setSegundosRestantes((prev) => {
        if (prev <= 1) {
          setCodigoDinamico(null); // Marcar como expirado
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(iv);
  }, [segundosRestantes > 0]);

  const formatoTiempo = `${String(Math.floor(segundosRestantes / 60)).padStart(2, '0')}:${String(segundosRestantes % 60).padStart(2, '0')}`;

  const totalLocales = useMemo(() => localesResumen.length, [localesResumen]);
  const totalPropios = useMemo(() => localesResumen.filter((item) => item.es_propio).length, [localesResumen]);
  const totalNoPropios = totalLocales - totalPropios;
  const totalGrupos = useMemo(() => {
    const grupos = new Set(localesResumen.map((item) => (item.grupo_id || '').trim()).filter(Boolean));
    return grupos.size;
  }, [localesResumen]);

  const abrirCredenciales = (local: LocalResumenItem) => {
    setSelectedLocal(local);
    setCredForm({ username: local.local_username || '', password: '' });
    setCredModalOpen(true);
    setError('');
  };

  const abrirEditarLocal = async (local: LocalResumenItem) => {
    let vigentes = arranques;
    if (esBanca && !local.parent_local_id) {
      try {
        vigentes = await fetchAPI<Record<string, Arranques>>('/usuarios/banca/montos-arranque');
        setArranques(vigentes);
      } catch {
        setError('No se pudo cargar el monto vigente del paquete. Intente nuevamente.');
        return;
      }
    }
    setEditingLocal(local);
    setArranqueForm({NORMAL: vigentes[local.local_id]?.NORMAL.personalizado ?? null, REVENTADO: vigentes[local.local_id]?.REVENTADO.personalizado ?? null});
    setEditForm({
      es_propio: local.es_propio,
      grupo_id: local.grupo_id || '',
      limite_saldo: local.limite_saldo != null ? String(local.limite_saldo) : '',
    });
    setError('');
    setEditModalOpen(true);
  };

  const guardarEdicionLocal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLocal) return;

    if (esBanca && !editingLocal.parent_local_id && (!arranques[editingLocal.local_id] || Object.values(arranqueForm).some(v => v !== null && (!/^\d+(\.\d{1,2})?$/.test(v) || Number(v) <= 0 || Number(v) > 99999999.99)))) {
      setError('Indique un monto personalizado positivo con máximo dos decimales.');
      return;
    }
    setSavingAction(true);
    setError('');
    try {
      await fetchAPI<LocalResumenItem>(`/usuarios/banca/locales/${editingLocal.local_id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...(esBanca && !editingLocal.parent_local_id ? {monto_arranque_normal_override: arranqueForm.NORMAL, monto_arranque_reventado_override: arranqueForm.REVENTADO} : {}),
          es_propio: editForm.es_propio,
          grupo_id: editForm.grupo_id.trim() || null,
          limite_saldo: !editForm.es_propio && editForm.limite_saldo.trim() ? Number(editForm.limite_saldo) : null,
        }),
      });

      setEditModalOpen(false);
      setEditingLocal(null);
      await cargarDatos();
    } catch (e: any) {
      setError(e.message || 'No se pudo actualizar el local');
    } finally {
      setSavingAction(false);
    }
  };

  const guardarCredenciales = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLocal) return;

    const username = credForm.username.trim();
    const password = credForm.password.trim();

    if (selectedLocal.local_archivado && (!username || password.length < 6)) {
      setError('Para crear un nuevo acceso indique usuario y contraseña de al menos 6 caracteres');
      return;
    }

    if (!username && !password) {
      setError('Debes indicar un usuario y/o contraseña');
      return;
    }

    setSavingAction(true);
    setError('');
    try {
      await fetchAPI<LocalResumenItem>(
        esMiniBanca
          ? `/usuarios/minibanca/vendedores/${selectedLocal.local_id}/credenciales`
          : `/usuarios/banca/locales/${selectedLocal.local_id}/credenciales`, {
        method: 'PATCH',
        body: JSON.stringify({
          username: username || undefined,
          password: password || undefined,
        }),
      });
      setCredModalOpen(false);
      setSelectedLocal(null);
      setCredForm({ username: '', password: '' });
      await cargarDatos();
    } catch (e: any) {
      setError(e.message || 'No se pudieron actualizar las credenciales del local');
    } finally {
      setSavingAction(false);
    }
  };

  const cambiarEstadoLocal = async (local: LocalResumenItem) => {
    const accion = local.local_activo ? 'inactivar' : 'activar';
    const mensaje = local.local_activo
      ? `¿Seguro que deseas inactivar ${local.nombre_local}?`
      : `¿Seguro que deseas activar ${local.nombre_local}?`;

    if (!window.confirm(mensaje)) return;

    setSavingAction(true);
    setError('');
    try {
      await fetchAPI<LocalResumenItem>(
        esMiniBanca
          ? `/usuarios/minibanca/vendedores/${local.local_id}/estado`
          : `/usuarios/banca/locales/${local.local_id}/estado`, {
        method: 'PATCH',
        body: JSON.stringify({ activo: !local.local_activo }),
      });
      await cargarDatos();
    } catch (e: any) {
      setError(e.message || `No se pudo ${accion} el local`);
    } finally {
      setSavingAction(false);
    }
  };

  const columns = [
    {
      key: 'local',
      header: 'Local',
      render: (l: LocalResumenItem) => (
        <div className="flex min-w-0 items-center gap-3">
          <div className="w-10 h-10 shrink-0 bg-gray-100 rounded-full flex items-center justify-center">
            <Building2 className="w-5 h-5 text-gray-500" />
          </div>
          <div className="min-w-0 break-words">
            <p className="font-medium text-gray-900">{l.nombre_local}</p>
            <p className="text-sm text-gray-500 break-all">{l.local_id}</p>
            {l.parent_local_id && <p className="text-sm text-gray-500">Mini Banca: {localesResumen.find((m) => m.local_id === l.parent_local_id)?.nombre_local || l.parent_local_id}</p>}
          </div>
        </div>
      ),
    },
    {
      key: 'tipo',
      header: 'Tipo',
      render: (l: LocalResumenItem) => {
        if (l.tipo_local === 'MINI_BANCA') {
          return <Badge variant="primary">MINI BANCA</Badge>;
        }
        return (
          <Badge variant={l.es_propio ? 'success' : 'neutral'}>
            {l.es_propio ? 'PROPIO' : 'NO PROPIO'}
          </Badge>
        );
      },
    },
    {
      key: 'arranque',
      header: esBanca ? 'Monto de arranque' : 'Grupo saldo',
      render: (l: LocalResumenItem) => esBanca ? <div className="text-xs space-y-1">
        {l.parent_local_id && <p className="text-gray-500">Heredado de Mini Banca</p>}
        {(['NORMAL', 'REVENTADO'] as const).map(tipo => {
          const info = arranques[l.local_id]?.[tipo];
          const efectivos = [...new Set(info?.reglas.filter(r => r.aplicable && r.efectivo !== null).map(r => r.efectivo!) || [])];
          return <div key={tipo}>{tipo}: {!info ? 'No disponible' : !info.reglas.some(r => r.aplicable) ? 'No aplica' : l.parent_local_id ? efectivos.length === 1 ? monedaArranque(efectivos[0]) : 'Según sorteo' : info.personalizado === null ? 'Por defecto' : monedaArranque(info.personalizado)}{l.parent_local_id && !info?.heredado_de && info?.reglas.some(r => r.aplicable) && <span> (regla interna)</span>}</div>;
        })}
      </div> : <span>{l.grupo_id || 'Sin grupo'}</span>,
    },
    {
      key: 'usuario_local',
      header: 'Usuario local',
      render: (l: LocalResumenItem) => <span className="font-medium">{l.local_username || '-'}</span>,
    },
    {
      key: 'estado',
      header: 'Estado',
      render: (l: LocalResumenItem) => (
        <Badge variant={l.local_activo ? 'success' : 'neutral'}>
          {l.local_archivado ? 'ARCHIVADO' : l.local_activo ? 'ACTIVO' : 'INACTIVO'}
        </Badge>
      ),
    },
    {
      key: 'acciones',
      header: 'Acciones',
      render: (l: LocalResumenItem) => (
        <div className="flex flex-wrap items-center gap-2 [&>button]:min-h-[44px]">
          {!esMiniBanca && (
            <Button size="sm" variant="secondary" leftIcon={<Pencil size={14} />} onClick={() => abrirEditarLocal(l)} disabled={savingAction}>
              <span>Editar</span>
            </Button>
          )}
          <Button size="sm" variant="secondary" leftIcon={<KeyRound size={14} />} onClick={() => abrirCredenciales(l)} disabled={savingAction || (esMiniBanca && l.local_archivado)}>
            <span>{l.local_archivado ? 'Crear nuevo acceso' : 'Credenciales'}</span>
          </Button>
          <Button size="sm" variant={l.local_activo ? 'warning' : 'success'} leftIcon={<Power size={14} />} onClick={() => cambiarEstadoLocal(l)} disabled={savingAction || l.local_archivado}>
            <span>{l.local_activo ? 'Inactivar' : 'Activar'}</span>
          </Button>
          {esBanca && l.local_archivado && (
            <Button size="sm" variant="danger" onClick={() => { setError(''); setEliminarSeleccionado(l); }} disabled={savingAction}>
              Eliminar definitivamente
            </Button>
          )}
          {esBanca && !l.local_archivado && (
            <Button size="sm" variant="danger" onClick={() => setRetirarSeleccionado(l)} disabled={savingAction}>
              {l.tipo_local === 'MINI_BANCA' ? 'Archivar Mini Banca' : 'Archivar'}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <Modal isOpen={!!eliminarSeleccionado} onClose={() => { if (!savingAction) setEliminarSeleccionado(null); }} title="Eliminar definitivamente" size="md">
        <p>Esta acción eliminará definitivamente el vendedor del sistema operativo. Sus obligaciones e historial financiero se conservarán. Esta acción no se puede deshacer.</p>
        {eliminarSeleccionado?.tipo_local === 'MINI_BANCA' && <p className="mt-2">Incluye la Mini Banca y todos sus vendedores.</p>}
        <p className="mt-2 font-semibold">{eliminarSeleccionado?.nombre_local}</p>
        {error && <p role="alert" className="text-red-600 mt-2">{error}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="secondary" disabled={savingAction} onClick={() => setEliminarSeleccionado(null)}>Cancelar</Button>
          <Button variant="danger" isLoading={savingAction} disabled={savingAction} onClick={eliminarDefinitivamente}>Eliminar definitivamente</Button>
        </div>
      </Modal>
      <Modal isOpen={!!retirarSeleccionado} onClose={() => { if (!savingAction) setRetirarSeleccionado(null); }} title={retirarSeleccionado?.tipo_local === 'MINI_BANCA' ? 'Archivar Mini Banca' : 'Archivar vendedor'} size="md">
        <p>{retirarSeleccionado?.tipo_local === 'MINI_BANCA'
          ? '¿Desea archivar esta Mini Banca? Se retirará el acceso de la Mini Banca y de sus vendedores. Sus saldos, deudas, ventas, movimientos e historial se conservarán.'
          : '¿Desea archivar este vendedor? El usuario perderá el acceso al sistema. Sus ventas, saldos, movimientos e historial se conservarán.'}</p>
        <p className="mt-2 font-semibold">{retirarSeleccionado?.nombre_local}</p>
        {error && <p role="alert" className="text-red-600 mt-2">{error}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="secondary" disabled={savingAction} onClick={() => setRetirarSeleccionado(null)}>Cancelar</Button>
          <Button variant="danger" isLoading={savingAction} disabled={savingAction} onClick={retirarLocal}>
            {retirarSeleccionado?.tipo_local === 'MINI_BANCA' ? 'Archivar Mini Banca' : 'Archivar'}
          </Button>
        </div>
      </Modal>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{esMiniBanca ? 'Mis vendedores' : 'Locales operativos'}</h1>
            <p className="text-gray-500">
              {esMiniBanca
                ? 'Vendedores internos de su Mini Banca (siempre NO propios)'
                : 'Cada local funciona como unidad de venta y branding independiente'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={cargarDatos} className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg" title="Refrescar">
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
            <Button variant="primary" leftIcon={<Plus size={18} />} onClick={() => setModalOpen(true)} disabled={limiteLocales.disponibles <= 0}>
              {esMiniBanca ? 'Nuevo Vendedor' : 'Nuevo Local'}
            </Button>
          </div>
        </div>

        {!esMiniBanca && <Card className="w-full">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Código dinámico</span>
              {segundosRestantes > 0 && <span className="text-xs text-gray-500">{formatoTiempo}</span>}
            </div>
            {codigoDinamico ? (
              <>
                <div className="text-3xl font-bold tracking-[0.2em] text-gray-900 text-center">{codigoDinamico.codigo}</div>
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" variant="secondary" onClick={copiarCodigo}>
                    {copiado ? 'Copiado' : 'Copiar'}
                  </Button>
                  <Button type="button" size="sm" variant="secondary" onClick={generarCodigoDinamico} isLoading={cargandoCodigo}>
                    Regenerar
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="text-sm text-gray-400 text-center py-1">{segundosRestantes <= 0 && codigoDinamico === null ? 'Sin código activo' : '------'}</div>
                <Button type="button" size="sm" variant="primary" onClick={generarCodigoDinamico} isLoading={cargandoCodigo} className="w-full">
                  Generar código
                </Button>
              </>
            )}
          </div>
        </Card>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
              <Store className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Locales</p>
              <p className="text-2xl font-bold text-gray-900">{totalLocales}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
              <Tag className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Propios</p>
              <p className="text-2xl font-bold text-green-600">{totalPropios}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">No propios</p>
              <p className="text-2xl font-bold text-gray-700">{totalNoPropios}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
              <Layers3 className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Grupos configurados</p>
              <p className="text-2xl font-bold text-purple-600">{totalGrupos}</p>
            </div>
          </div>
        </Card>
      </div>

      {error && <p className="text-sm text-danger-600">{error}</p>}

      <Card padding="none" className="hidden md:block">
        <DataTable columns={columns} data={localesResumen} keyExtractor={(l: LocalResumenItem) => l.local_id} isLoading={loading} emptyMessage="No hay locales registrados en esta banca" />
      </Card>

      <div className="md:hidden space-y-3" role="region" aria-label="Locales en pantalla móvil" aria-busy={loading}>
        {loading ? <p className="text-center py-6 text-gray-500">Cargando locales…</p> : localesResumen.length === 0 ?
          <p className="text-center py-6 text-gray-500">No hay locales registrados en esta banca</p> :
          localesResumen.map(local => <Card key={local.local_id} className="min-w-0 break-words">
            <dl className="space-y-3">
              {columns.map(columna => <div key={columna.key} className="min-w-0">
                <dt className="text-xs font-semibold text-gray-500 mb-1">{columna.header}</dt>
                <dd className="min-w-0 break-words">{columna.render(local)}</dd>
              </div>)}
            </dl>
          </Card>)}
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={esMiniBanca ? 'Crear vendedor interno' : 'Crear nuevo local'} size="md">
        <form onSubmit={crearLocal} className="space-y-4">
          <Input label="Nombre" value={form.nombre} onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))} required />
          <Input label="Dirección (opcional)" value={form.direccion} onChange={(e) => setForm((p) => ({ ...p, direccion: e.target.value }))} />
          <Input label="Teléfono (opcional)" value={form.telefono} onChange={(e) => setForm((p) => ({ ...p, telefono: e.target.value }))} />
          {!esMiniBanca && form.tipo_selector !== 'MINI_BANCA' && (
            <Input label="Grupo de saldo (opcional)" value={form.grupo_id} onChange={(e) => setForm((p) => ({ ...p, grupo_id: e.target.value }))} placeholder="Ej: Grupo A" />
          )}

          {!esMiniBanca && <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Tipo de local</p>
            <div className="grid grid-cols-3 gap-2">
              {(['PROPIO', 'NO_PROPIO', 'MINI_BANCA'] as TipoSelector[]).map((opt) => {
                const labels: Record<TipoSelector, string> = { PROPIO: 'Propio', NO_PROPIO: 'No propio', MINI_BANCA: 'Mini Banca' };
                const active = form.tipo_selector === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, tipo_selector: opt, limite_saldo: '' }))}
                    className={`py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                      active
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'
                    }`}
                  >
                    {labels[opt]}
                  </button>
                );
              })}
            </div>
          </div>}

          {(esMiniBanca || form.tipo_selector === 'NO_PROPIO') && (
            <Input
              label="Límite de saldo/crédito (opcional)"
              type="number"
              value={form.limite_saldo}
              onChange={(e) => setForm((p) => ({ ...p, limite_saldo: e.target.value }))}
              placeholder="Ej: 100000"
            />
          )}

          <div className="rounded-lg bg-primary-50 px-3 py-2 text-sm text-primary-800">
            {esMiniBanca
              ? 'El vendedor interno será SIEMPRE NO PROPIO y quedará asociado a su Mini Banca.'
              : form.tipo_selector === 'MINI_BANCA'
                ? 'La Mini Banca podrá crear y gestionar sus propios vendedores internos.'
                : 'Cada local crea su propio usuario operativo y funciona como una unidad de venta independiente.'}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit" variant="primary" isLoading={savingLocal}>Crear local</Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingLocal(null);
        }}
        title={`Editar local${editingLocal ? `: ${editingLocal.nombre_local}` : ''}`}
        size="md"
      >
        <form onSubmit={guardarEdicionLocal} className="space-y-4">
          {esBanca && editingLocal?.parent_local_id && <section className="min-w-0 break-words border rounded-lg p-3 space-y-2">
            <h3 className="font-semibold">Monto heredado de Mini Banca</h3>
            <p className="text-sm">Cada vendedor hereda el monto completo, sujeto al cupo individual y al disponible del grupo. Se configura en Mini Banca.</p>
            {(['NORMAL', 'REVENTADO'] as const).map(tipo => <div key={tipo} className="text-sm">
              {!arranques[editingLocal.local_id]?.[tipo].heredado_de && <p>Sin arranque heredado activo: se conserva la regla interna aplicable.</p>}
              <strong>{tipo}: </strong>{arranques[editingLocal.local_id]?.[tipo].reglas.map(r => <p key={r.sorteo}>{r.sorteo}: {r.aplicable && r.efectivo !== null ? monedaArranque(r.efectivo) : 'Sin restricción aplicable'}</p>) || 'No disponible'}
            </div>)}
          </section>}
          {esBanca && editingLocal && !editingLocal.parent_local_id && <section className="min-w-0 break-words border rounded-lg p-3 space-y-3">
            <h3 className="font-semibold">Monto de arranque de restringidos</h3>
            {editingLocal.tipo_local === 'MINI_BANCA' && <p className="text-sm text-gray-500">Los vendedores hijos heredan automáticamente estos montos, sujetos al cupo disponible del grupo.</p>}
            {(['NORMAL', 'REVENTADO'] as const).map(tipo => {
              const info = arranques[editingLocal.local_id]?.[tipo];
              const aplicable = info?.reglas.some(r => r.aplicable) || false;
              const valores = [...new Set(info?.reglas.map(r => r.paquete) || [])];
              const heredado = !aplicable ? 'Sin restricción aplicable' : valores.length === 1 && valores[0] !== null ? monedaArranque(valores[0]) : 'Según sorteo';
              return <fieldset key={tipo} className="min-w-0 space-y-2 border-t pt-2" disabled={editingLocal.local_archivado || !info}>
                <legend className="font-medium">{tipo}</legend>
                <p className="text-sm">Monto definido por paquete: {heredado}</p>
                {aplicable && valores.length > 1 && info?.reglas.map(r => <p key={r.sorteo} className="text-xs text-gray-500">{r.sorteo}: {r.paquete === null ? 'Sin restricción aplicable' : monedaArranque(r.paquete)}</p>)}
                {aplicable && <div className="grid grid-cols-1 gap-2 text-sm">{[false, true].map(personal => <label key={String(personal)} className="flex min-h-[44px] items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50">
                  <input type="radio" name={`arranque-${tipo}`} checked={(arranqueForm[tipo] !== null) === personal}
                    className="h-4 w-4 shrink-0" onChange={() => setArranqueForm(p => ({...p, [tipo]: personal ? '' : null}))} /> {personal ? 'Personalizado' : 'Por defecto del paquete'}
                </label>)}</div>}
                {aplicable && arranqueForm[tipo] !== null && <Input label={`Monto personalizado ${tipo}`} type="number" inputMode="decimal" min="0.01" max="99999999.99" step="0.01" required value={arranqueForm[tipo]!}
                  onChange={e => setArranqueForm(p => ({...p, [tipo]: e.target.value}))} />}
                <p className="text-sm">Monto efectivo: {aplicable ? arranqueForm[tipo] === null ? heredado : arranqueForm[tipo] ? monedaArranque(arranqueForm[tipo]!) : 'Pendiente' : 'Sin restricción aplicable'}</p>
                {!aplicable && <p className="text-xs text-gray-500">El monto personalizado no crea restricciones nuevas.</p>}
              </fieldset>;
            })}
          </section>}

          {editingLocal?.tipo_local !== 'MINI_BANCA' && (
            <Input
              label="Grupo de saldo"
              value={editForm.grupo_id}
              onChange={(e) => setEditForm((prev) => ({ ...prev, grupo_id: e.target.value }))}
              placeholder="Ej: Grupo A"
            />
          )}

          {editingLocal?.tipo_local !== 'MINI_BANCA' && (
            <label className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={editForm.es_propio}
                onChange={(e) => setEditForm((prev) => ({ ...prev, es_propio: e.target.checked, limite_saldo: e.target.checked ? '' : prev.limite_saldo }))}
                className="h-4 w-4"
              />
              Local propio (imprime logo en ticket)
            </label>
          )}

          {editingLocal?.tipo_local === 'MINI_BANCA' && (
            <div className="rounded-lg bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
              Las Mini Bancas son siempre no propias y no pertenecen a un grupo de saldo.
            </div>
          )}

          {editingLocal?.tipo_local !== 'MINI_BANCA' && !editForm.es_propio && (
            <Input
              label="Límite de saldo/crédito (opcional)"
              type="number"
              value={editForm.limite_saldo}
              onChange={(e) => setEditForm((prev) => ({ ...prev, limite_saldo: e.target.value }))}
              placeholder="Ej: 100000"
            />
          )}

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2 [&>button]:min-h-[44px]">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setEditModalOpen(false);
                setEditingLocal(null);
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" isLoading={savingAction}>Guardar cambios</Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={credModalOpen}
        onClose={() => {
          setCredModalOpen(false);
          setSelectedLocal(null);
          setCredForm({ username: '', password: '' });
        }}
        title={`Credenciales local${selectedLocal ? `: ${selectedLocal.nombre_local}` : ''}`}
        size="md"
      >
        <form onSubmit={guardarCredenciales} className="space-y-4">
          <Input label="Usuario" value={credForm.username} onChange={(e) => setCredForm((p) => ({ ...p, username: e.target.value }))} placeholder="Usuario del local" />
          <Input label={selectedLocal?.local_archivado ? 'Contraseña del nuevo acceso' : 'Nueva contraseña (opcional)'} type="password" value={credForm.password} onChange={(e) => setCredForm((p) => ({ ...p, password: e.target.value }))} placeholder="Mínimo 6 caracteres" />

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => {
              setCredModalOpen(false);
              setSelectedLocal(null);
              setCredForm({ username: '', password: '' });
            }}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" isLoading={savingAction}>Guardar</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default BancaVendedoresPage;
